/**
 * api.client.ts — D.1.3 Axios API Client with Interceptors
 *
 * This module provides a factory function `createApiClient` that wires up:
 *   - D.1.3.3 Token injection interceptor (Authorization: Bearer <token>)
 *   - D.1.3.4 401 response interceptor (token refresh + retry)
 *   - D.1.3.5 Request ID injection interceptor (x-request-id)
 *
 * Design decisions:
 *   - Factory function (not module singleton) — enables dependency injection
 *     in tests. The production singleton `apiClient` is exported below.
 *   - Token getter is injected — decouples the client from Firebase directly
 *     so both can be tested in isolation.
 *   - Retry flag stored in request config — prevents infinite refresh loops
 *     if the backend returns 401 even after refresh.
 *
 * Usage:
 *   import { apiClient } from '@/services/api.client';
 *   const response = await apiClient.get<User>('/users/me');
 */
import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { authService } from './auth.service';
import { stripApiPrefix } from './api/paths';
import { normalizeBody, type PageRequest } from './api/envelope';
import { translateRequest, type DjangoRoute } from './api/django-routes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Which wire contract the backend speaks.
 *   'envelope' — demo MSW handlers (and the legacy Express API): bodies are
 *                already `{success, data, meta}`; the client changes nothing.
 *   'django'   — admin-backend DRF API: paths/params are translated through
 *                the route table and bodies normalised to the envelope.
 */
export type ApiDialect = 'envelope' | 'django';

/**
 * Extends AxiosRequestConfig to track whether we have already retried a
 * request after a 401 — prevents infinite retry loops.
 */
interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
  /** Set by the django-dialect request interceptor; read by the response one. */
  _djangoRoute?: DjangoRoute;
  _djangoPage?: PageRequest;
  _djangoTranslated?: boolean;
}

/** Options for createApiClient factory. */
export interface ApiClientOptions {
  /** Base URL for all requests (defaults to VITE_API_URL). */
  baseURL: string;
  /**
   * Async function that returns the current auth token, or null when the user
   * is not authenticated. Injected for testability.
   */
  getToken: () => Promise<string | null>;
  /**
   * Forces a token refresh and returns the new token.
   * Called by the 401 interceptor before retrying.
   */
  forceRefreshToken?: () => Promise<string | null>;
  /**
   * Called when a token refresh fails (e.g., refresh token is expired).
   * Typically triggers a redirect to the login page.
   */
  onRefreshFailure: () => void;
  /** Wire contract of the backend. Defaults to 'envelope' (demo behaviour). */
  dialect?: ApiDialect;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Creates a new Axios instance with the PhiloFund interceptors attached.
 *
 * Exported for unit-testing with injected dependencies. Application code
 * should import the `apiClient` singleton at the bottom of this file.
 */
export function createApiClient(options: ApiClientOptions): AxiosInstance {
  const { baseURL, getToken, forceRefreshToken, onRefreshFailure } = options;
  const dialect: ApiDialect = options.dialect ?? 'envelope';

  const instance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    // 30-second timeout — generous for mobile connections
    timeout: 30_000,
  });

  // ---- Request interceptor: Authorization + x-request-id ---------------

  instance.interceptors.request.use(
    async (config: RetryableRequestConfig): Promise<InternalAxiosRequestConfig> => {
      // Base-URL join: hooks say '/api/<resource>', the base URL is the API
      // root — drop the marker so it is not doubled ('/api/api/...').
      config.url = stripApiPrefix(config.url, config.baseURL);

      // Django dialect: translate path + params through the route table.
      // Guarded so a 401 retry of the same config is not translated twice.
      if (dialect === 'django' && config.url !== undefined && !config._djangoTranslated) {
        const t = translateRequest(config.url, config.params as Record<string, unknown> | undefined);
        config.url = t.url;
        config.params = t.params;
        config._djangoTranslated = true;
        if (t.route !== undefined) config._djangoRoute = t.route;
        if (t.page !== undefined) config._djangoPage = t.page;
        if (t.dropped.length > 0) {
          console.warn(
            `[api] Django ${t.route?.name ?? 'route'} ignores param(s): ${t.dropped.join(', ')} — results are NOT filtered by them.`,
          );
        }
      }

      // D.1.3.5 — Unique request ID for distributed tracing / log correlation
      const requestId = generateRequestId();
      config.headers.set('x-request-id', requestId);

      // D.1.3.3 — Inject token only when the user is authenticated
      const token = await getToken();
      if (token !== null) {
        config.headers.set('Authorization', `Bearer ${token}`);
      }

      return config;
    },
    (error: unknown) => Promise.reject(error),
  );

  // ---- Response interceptor: 401 token-refresh + retry -----------------

  instance.interceptors.response.use(
    // 'envelope': pass through unchanged. 'django': normalise the DRF body to
    // the {success, data, meta} envelope the hooks read.
    (response) => {
      if (dialect !== 'django') return response;
      const config = response.config as RetryableRequestConfig;
      const requestId = String(config.headers.get('x-request-id') ?? '');
      response.data = normalizeBody(response.data, {
        requestId,
        ...(config._djangoPage !== undefined ? { page: config._djangoPage } : {}),
        ...(config._djangoRoute?.mapItem !== undefined ? { mapItem: config._djangoRoute.mapItem } : {}),
      });
      return response;
    },

    async (error: unknown): Promise<never> => {
      // Only handle Axios errors with a 401 status
      if (!isAxiosError(error)) {
        return Promise.reject(error);
      }

      const originalRequest = error.config as RetryableRequestConfig | undefined;

      if (
        error.response?.status === 401 &&
        originalRequest !== undefined &&
        !originalRequest._retried
      ) {
        // Mark so we don't retry indefinitely
        originalRequest._retried = true;

        try {
          // D.1.3.4 — Force refresh and retry with new token
          const refreshFn = forceRefreshToken ?? (() => authService.getToken(true));
          const freshToken = await refreshFn();

          if (freshToken !== null) {
            originalRequest.headers.set('Authorization', `Bearer ${freshToken}`);
          }

          return instance.request(originalRequest) as Promise<never>;
        } catch (_refreshError) {
          // Refresh failed — user session is truly expired
          onRefreshFailure();
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    },
  );

  return instance;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Generates a URL-safe unique request ID for distributed tracing.
 *
 * Uses crypto.randomUUID when available (modern browsers + Node 18+),
 * falls back to a timestamp-based ID for environments that don't support it.
 */
function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback: timestamp + random hex suffix — not UUID-spec but unique enough
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Type-safe Axios error predicate.
 */
function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

// ---------------------------------------------------------------------------
// Token acquisition — the ONE place a login strategy plugs in
// ---------------------------------------------------------------------------

/** VITE_API_BACKEND=django selects the Django dialect (.env.live). */
const apiDialect: ApiDialect = import.meta.env.VITE_API_BACKEND === 'django' ? 'django' : 'envelope';

/**
 * Returns the bearer token to send, or null to send none.
 *
 * 'envelope' (demo / legacy): the Firebase ID token, exactly as before.
 *
 * 'django': NO token yet. Django cannot verify a Firebase ID token (gap map
 * X1), and the strategy that replaces it — interim SimpleJWT bearer tokens vs
 * ADR-016 OIDC — is an open operator decision. Whichever is chosen is
 * implemented HERE and nowhere else; until then live mode sends no
 * Authorization header, which is exactly what the public screens need.
 */
export async function acquireAccessToken(forceRefresh: boolean): Promise<string | null> {
  if (apiDialect === 'django') return null;
  return authService.getToken(forceRefresh);
}

// ---------------------------------------------------------------------------
// Production singleton
// ---------------------------------------------------------------------------

/**
 * Pre-configured Axios instance for use throughout the application.
 *
 * Wired to:
 *   - VITE_API_URL as the base URL
 *   - VITE_API_BACKEND as the dialect ('django' in live mode)
 *   - acquireAccessToken for token injection and forced refresh
 *   - A window-location redirect to '/login' on refresh failure
 */
export const apiClient: AxiosInstance = createApiClient({
  baseURL: import.meta.env.VITE_API_URL,
  dialect: apiDialect,
  getToken: () => acquireAccessToken(false),
  forceRefreshToken: () => acquireAccessToken(true),
  onRefreshFailure: () => {
    // Clear any cached state before redirecting — the Zustand auth store
    // listener will handle this via onAuthStateChanged, but we redirect
    // immediately for a snappy UX.
    window.location.href = '/login?reason=session-expired';
  },
});

export default apiClient;
